import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../../_services/auth.service';
import {
  ClinicianReference,
  DetailLevel,
  FormatMode,
  NoteGenerationSettings,
  PatientReference,
  QuotationPolicy,
} from '../../../_interfaces/note-generation.interface';
import { withAbbreviationToggle } from '../../../_helpers/note-generation';
import {
  diffStyleSettings,
  styleSettingChangedProps,
  styleSettingsSavedProps,
} from '../../../_helpers/style-settings-analytics';
import { AnalyticsService } from '../../../_services/analytics.service';
import { User } from '../../../_interfaces/user';
import { OptionSliderComponent } from '../../../shared/components/option-slider/option-slider.component';
import { OptionSliderOptions } from '../../../shared/components/option-slider/option-slider.types';
import { LoadingSpinnerComponent } from '../../../shared/components/loading-spinner/loading-spinner.component';

@Component({
  selector: 'app-default-style-settings',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatRadioModule,
    MatSelectModule,
    TranslateModule,
    OptionSliderComponent,
    LoadingSpinnerComponent,
  ],
  templateUrl: './default-style-settings.component.html',
})
export class DefaultStyleSettingsComponent {
  private readonly analytics = inject(AnalyticsService);
  user: User | null = null;
  readonly styleForm: FormGroup;
  isLoading = false;
  savedSettings: NoteGenerationSettings | null = null;
  private trackedSettings: NoteGenerationSettings | null = null;

  readonly detailLevelOptions: OptionSliderOptions[] = [
    { value: DetailLevel.Brief, label: 'Brief' },
    { value: DetailLevel.Balanced, label: 'Balanced (default)' },
    { value: DetailLevel.Comprehensive, label: 'Comprehensive' },
  ];

  readonly quotationOptions: OptionSliderOptions[] = [
    { value: QuotationPolicy.None, label: 'None' },
    { value: QuotationPolicy.SalientOnly, label: 'Salient only (default)' },
    { value: QuotationPolicy.Liberal, label: 'Liberal' },
  ];

  readonly formatModeOptions: OptionSliderOptions[] = [
    { value: FormatMode.Structured, label: 'Structured' },
    { value: FormatMode.Hybrid, label: 'Hybrid' },
    { value: FormatMode.Prose, label: 'Prose' },
  ];

  readonly clinicianOptions: OptionSliderOptions[] = [
    { value: ClinicianReference.FirstPerson, label: 'First person (default)' },
    { value: ClinicianReference.Passive, label: 'Passive' },
    { value: ClinicianReference.ThirdPerson, label: 'Third person' },
  ];

  readonly patientOptions: OptionSliderOptions[] = [
    { value: PatientReference.NameIfAvailableElseThey, label: "Name if available, else 'they' (default)" },
    { value: PatientReference.NameOnly, label: 'Name only' },
    { value: PatientReference.They, label: 'They' },
  ];

  get hasChanges(): boolean {
    if (!this.savedSettings) {
      return false;
    }
    const current = this.styleForm.getRawValue() as NoteGenerationSettings;
    return (Object.keys(this.savedSettings) as (keyof NoteGenerationSettings)[])
      .some(key => current[key] !== this.savedSettings?.[key]);
  }

  constructor(
    private readonly authService: AuthService,
    private readonly fb: FormBuilder,
    private readonly snackBar: MatSnackBar,
    private readonly translate: TranslateService
  ) {
    this.styleForm = this.fb.group({
      detailLevel: [''],
      formatMode: [''],
      abbreviationPolicy: [''],
      quotationPolicy: [''],
      emptySectionPolicy: [''],
      referClinicianAs: [''],
      referPatientAs: [''],
    });
    this.styleForm.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(value => this.trackFieldChanges(value as NoteGenerationSettings));
    this.user = this.authService.getUser();
    this.setForm();
  }

  setForm(): void {
    const settings = this.user?.settings?.noteGeneration;
    if (!settings) {
      return;
    }
    this.savedSettings = withAbbreviationToggle({ ...settings });
    this.trackedSettings = { ...this.savedSettings };
    this.styleForm.reset(this.savedSettings, { emitEvent: false });
  }

  onSliderChange(controlName: keyof NoteGenerationSettings, value: string): void {
    this.styleForm.get(controlName)?.setValue(value);
  }

  cancel(): void {
    this.analytics.track('style_settings_cancelled', {
      surface: 'settings_page',
      had_unsaved_changes: this.hasChanges,
    });
    this.setForm();
  }

  async saveChanges(): Promise<void> {
    if (!this.hasChanges || !this.savedSettings) {
      return;
    }

    const before = { ...this.savedSettings };
    const after = withAbbreviationToggle({
      ...this.savedSettings,
      ...this.styleForm.getRawValue(),
    });

    this.trackSettingsSaved(before, after);

    this.isLoading = true;
    let message = this.translate.instant('Style settings saved successfully');
    try {
      this.user = await this.authService.updateUser({
        settings: {
          noteGeneration: after,
        },
      } as User);
      this.setForm();
    } catch (e) {
      console.error('Failed to save style settings:', e);
      message = this.translate.instant('Unable to save style settings');
    } finally {
      this.isLoading = false;
    }

    this.snackBar.open(message, this.translate.instant('Close'), {
      duration: 2000,
      horizontalPosition: 'right',
      verticalPosition: 'bottom',
    });
  }

  // Analytics helpers

  private trackSettingsSaved(before: NoteGenerationSettings, after: NoteGenerationSettings): void {
    const changes = diffStyleSettings(before, after, 'settings_page');
    this.analytics.track('style_settings_saved', styleSettingsSavedProps('settings_page', changes));
  }

  private trackFieldChanges(current: NoteGenerationSettings): void {
    if (!this.trackedSettings) {
      this.trackedSettings = { ...current };
      return;
    }
    const before = this.trackedSettings;
    const after = { ...before, ...current };
    this.trackedSettings = after;
    for (const change of diffStyleSettings(before, after, 'settings_page')) {
      this.analytics.track('style_setting_changed', styleSettingChangedProps('settings_page', change));
    }
  }
}
